import { Button, Modal } from '@braskai/ui-kit';

import { alpha, css, keyframes, styled } from '@mui/material';

const REDUCED_MOTION_QUERY = '@media (prefers-reduced-motion: reduce)';

// The click point the preview zooms toward, as in the design: the "Get started" button of the mock page.
const CLICK_X = '38%';
const CLICK_Y = '64%';
const ZOOM_CYCLE = '5.4s cubic-bezier(0.4, 0, 0.2, 1) infinite';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

const azZoom = keyframes`
  0%, 12% { transform: scale(1); }
  34% { transform: scale(1.42); }
  68% { transform: scale(1.42); }
  90%, 100% { transform: scale(1); }
`;

const azRipple = keyframes`
  0%, 8% { transform: translate(-50%, -50%) scale(0.35); opacity: 0; }
  13% { opacity: 0.65; }
  34% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
  100% { opacity: 0; }
`;

const azCursor = keyframes`
  0%, 9% { transform: scale(1); }
  13% { transform: scale(0.8); }
  22% { transform: scale(1); }
  100% { transform: scale(1); }
`;

const recPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.78); }
`;

export const SDialog = styled(Modal)(
  ({ theme }) => css`
    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.45)};
      backdrop-filter: blur(3px);
    }

    .MuiDialog-paper {
      width: 100%;
      max-width: 440px;
      border-radius: 20px;
      background-color: transparent;
      box-shadow:
        0 24px 64px -12px ${alpha(theme.palette.grey[900], 0.28)},
        0 0 0 1px ${alpha(theme.palette.grey[900], 0.04)};
    }
  `,
);

export const SCard = styled('div')(
  ({ theme }) => css`
    position: relative;
    padding: 28px;
    border-radius: 20px;
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[900]};
    font-family: ${theme.typography.fontFamily};
    animation: ${fadeUp} 0.22s ease;

    ${REDUCED_MOTION_QUERY} {
      animation: none;
    }
  `,
);

export const SCloseButton = styled('button')(
  ({ theme }) => css`
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    background: transparent;
    cursor: pointer;

    &:hover {
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.grey[100]};
    }
  `,
);

export const SBadge = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    height: 26px;
    /* Pulls the title up: the badge and the title read as one heading block. */
    margin: 24px 0 -10px;
    padding: 0 12px;
    border-radius: 999px;
    background-color: ${theme.palette.primary[50]};
    color: ${theme.palette.primary[700]};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.07em;
    white-space: nowrap;
  `,
);

export const SBadgeDot = styled('span')(
  ({ theme }) => css`
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${theme.palette.primary[600]};
  `,
);

export const STitle = styled('h3')(
  ({ theme }) => css`
    margin: 24px 0 0;
    padding-right: 8px;
    color: ${theme.palette.grey[900]};
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.015em;
    text-wrap: pretty;
  `,
);

export const SPreview = styled('div')(
  ({ theme }) => css`
    position: relative;
    height: 186px;
    margin: 16px 0 24px;
    overflow: hidden;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 14px;
    background-color: ${theme.palette.grey[50]};
  `,
);

export const SWindow = styled('div')(
  ({ theme }) => css`
    position: absolute;
    inset: 18px;
    overflow: hidden;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 10px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 8px 22px -10px ${alpha(theme.palette.grey[900], 0.35)};
  `,
);

// Scales the mock page toward the click point; the ripple and the cursor stay unscaled on top of it.
export const SZoomLayer = styled('div')`
  position: absolute;
  inset: 0;
  transform-origin: ${CLICK_X} ${CLICK_Y};
  animation: ${azZoom} ${ZOOM_CYCLE};

  ${REDUCED_MOTION_QUERY} {
    animation: none;
  }
`;

export const SWindowBar = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 5px;
    height: 22px;
    padding: 0 11px;
    border-bottom: 1px solid ${theme.palette.grey[100]};
    background-color: ${theme.palette.grey[50]};

    & > span {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: ${theme.palette.grey[300]};
    }
  `,
);

export const SSkeleton = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
`;

export const SSkeletonLine = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone' && prop !== '$width',
})<{ $tone: 100 | 200; $width: string }>(
  ({ theme, $tone, $width }) => css`
    width: ${$width};
    height: 8px;
    border-radius: 999px;
    background-color: ${theme.palette.grey[$tone]};
  `,
);

export const SClickTarget = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: ${CLICK_Y};
    left: ${CLICK_X};
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 12px;
    border-radius: 8px;
    background-color: ${theme.palette.primary[600]};
    color: ${theme.palette.common.white};
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  `,
);

export const SRipple = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: ${CLICK_Y};
    left: ${CLICK_X};
    width: 38px;
    height: 38px;
    box-sizing: border-box;
    border: 2px solid ${theme.palette.primary[500]};
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: ${azRipple} ${ZOOM_CYCLE};

    /* Without motion a still ring keeps marking the click. */
    ${REDUCED_MOTION_QUERY} {
      animation: none;
      opacity: 0.65;
    }
  `,
);

export const SCursor = styled('svg')(
  ({ theme }) => css`
    position: absolute;
    top: calc(${CLICK_Y} + 4px);
    left: calc(${CLICK_X} + 6px);
    color: ${theme.palette.grey[900]};
    transform-origin: top left;
    animation: ${azCursor} ${ZOOM_CYCLE};

    & path {
      fill: currentColor;
      stroke: ${theme.palette.common.white};
    }

    ${REDUCED_MOTION_QUERY} {
      animation: none;
    }
  `,
);

export const SCameraBubble = styled('span')(
  ({ theme }) => css`
    position: absolute;
    right: 14px;
    bottom: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    box-sizing: border-box;
    border: 2px solid ${theme.palette.common.white};
    border-radius: 50%;
    background-color: ${theme.palette.grey[900]};
    color: ${theme.palette.common.white};
    box-shadow: 0 6px 16px -6px ${alpha(theme.palette.grey[900], 0.5)};
  `,
);

export const SRecBadge = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 12px;
    left: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 24px;
    padding: 0 11px;
    border-radius: 999px;
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[800]};
    box-shadow: 0 4px 12px -4px ${alpha(theme.palette.grey[900], 0.28)};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  `,
);

export const SRecDot = styled('span')(
  ({ theme }) => css`
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.palette.error[500]};
    animation: ${recPulse} 1.6s ease-in-out infinite;

    ${REDUCED_MOTION_QUERY} {
      animation: none;
    }
  `,
);

export const SDescription = styled('p')(
  ({ theme }) => css`
    margin: 0 0 24px;
    color: ${theme.palette.grey[500]};
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    text-wrap: pretty;
  `,
);

export const SCtaButton = styled(Button)(
  ({ theme }) => css`
    gap: 8px;
    height: 48px;
    border-radius: 12px;
    box-shadow: none;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;

    /* The kit Button wraps its children in a span, so the icon/label gap has to live on that span. */
    & > span {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    &:hover {
      border-color: ${theme.palette.primary[700]};
      background-color: ${theme.palette.primary[700]};
    }
  `,
);
